/**
 * GenerationLayoutExclusionControls — no-build exclusion zones for the parcel
 * test fit.
 *
 * Fetches FEMA NFHL flood polygons (100-yr + optional 500-yr, floodway always)
 * and snapshots them into the layout configuration; the deterministic solver
 * consumes only the snapshot so published exhibits re-validate. Site constraint
 * layers (category constraint.environmental) can be added as custom zones.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CloudRain, Mountain, ShieldX, Sprout, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { deriveSteepSlopeExclusionZones } from '../../../lib/generationImpact/generationLayoutSlopeExclusions';
import { sampleElevationCascading } from '../../../services/dataProviders/elevation/cascadingSampler';
import { fetchFemaExclusionZones } from '../../../services/dataProviders/femaFloodExclusions';
import { fetchNwiWetlandExclusionZones } from '../../../services/dataProviders/nwiWetlandExclusions';
import { computeLayerContentHash } from '../../../services/kmzParser';
import type {
  GenerationLayoutConfiguration,
  GenerationLayoutExclusionSet,
  GenerationLayoutExclusionZone,
} from '../../../types/generationImpact';
import type { SiteBoundaryParcel } from '../../../types/siteBoundary';
import type { SiteLayer } from '../../../types/site';

export interface GenerationLayoutExclusionControlsProps {
  layout: GenerationLayoutConfiguration;
  parcel: SiteBoundaryParcel | null;
  siteLayers: readonly SiteLayer[];
  /** Composite `${siteId}:${scenarioId ?? 'draft'}` identity. Analyses started
   * for another Studio session are aborted even when parcel bytes match. */
  sessionKey?: string;
  readOnly: boolean;
  onPatch: (patch: Partial<GenerationLayoutConfiguration>) => void;
}

type ExclusionAnalysisKind = 'fema' | 'wetlands' | 'slope';

interface ExclusionAnalysisRequest {
  kind: ExclusionAnalysisKind;
  requestId: number;
  sessionKey: string;
  inputFingerprint: string;
  controller: AbortController;
}

function supersededRequestReason(): Error {
  const error = new Error('Parcel exclusion analysis superseded');
  error.name = 'AbortError';
  return error;
}

function siteLayerZone(layer: SiteLayer): GenerationLayoutExclusionZone {
  return {
    id: `site-layer-${layer.id}`,
    label: layer.name,
    source: 'site-layer',
    kind: 'custom',
    polygons: [layer.geometry.coordinates.map(point => ({ ...point }))],
    provenance: {
      providerId: layer.source,
      retrievedAt: layer.importedAt,
      contentHash: computeLayerContentHash(layer),
    },
  };
}

export const GenerationLayoutExclusionControls: React.FC<GenerationLayoutExclusionControlsProps> = ({
  layout,
  parcel,
  siteLayers,
  sessionKey = 'unknown-site:draft',
  readOnly,
  onPatch,
}) => {
  const exclusions = layout.exclusions;
  const [include500yr, setInclude500yr] = useState(exclusions?.include500yr ?? true);
  const [fetching, setFetching] = useState(false);
  const [fetchingWetlands, setFetchingWetlands] = useState(false);
  const [derivingSlope, setDerivingSlope] = useState(false);
  const nextRequestIdRef = useRef(0);
  const requestsRef = useRef<Partial<Record<ExclusionAnalysisKind, ExclusionAnalysisRequest>>>({});
  const latestExclusionsRef = useRef(exclusions);
  const readOnlyRef = useRef(readOnly);
  latestExclusionsRef.current = exclusions;
  readOnlyRef.current = readOnly;

  const constraintLayers = useMemo(() => siteLayers.filter(layer => (
    layer.category === 'constraint.environmental'
    && layer.geometry.type === 'polygon'
    && layer.geometry.coordinates.length >= 3
  )), [siteLayers]);

  const nonFemaZones = useMemo(
    () => (exclusions?.zones ?? []).filter(zone => zone.source !== 'fema-nfhl'),
    [exclusions],
  );
  const femaZones = useMemo(
    () => (exclusions?.zones ?? []).filter(zone => zone.source === 'fema-nfhl'),
    [exclusions],
  );

  // Analyses bind to the entire session snapshot, not merely the parcel ID.
  // This catches scenario swaps, geometry edits, site-layer changes, and
  // concurrent layout edits before an older response can mutate the Studio.
  const scopeFingerprint = useMemo(() => JSON.stringify({
    sessionKey,
    parcel: parcel
      ? { id: parcel.id, polygon: parcel.polygon }
      : null,
    layout,
    siteLayers: siteLayers.map(layer => ({
      id: layer.id,
      category: layer.category,
      source: layer.source,
      importedAt: layer.importedAt,
      geometry: layer.geometry,
    })),
  }), [layout, parcel, sessionKey, siteLayers]);
  const femaInputFingerprint = useMemo(
    () => JSON.stringify({ scopeFingerprint, include500yr }),
    [include500yr, scopeFingerprint],
  );
  const currentFingerprintsRef = useRef<Record<ExclusionAnalysisKind, string>>({
    fema: femaInputFingerprint,
    wetlands: scopeFingerprint,
    slope: scopeFingerprint,
  });
  currentFingerprintsRef.current = {
    fema: femaInputFingerprint,
    wetlands: scopeFingerprint,
    slope: scopeFingerprint,
  };

  const abortPendingRequests = useCallback(() => {
    for (const request of Object.values(requestsRef.current)) {
      request?.controller.abort(supersededRequestReason());
    }
    requestsRef.current = {};
  }, []);

  useEffect(() => {
    abortPendingRequests();
    setFetching(false);
    setFetchingWetlands(false);
    setDerivingSlope(false);
  }, [abortPendingRequests, femaInputFingerprint, readOnly, scopeFingerprint]);

  useEffect(() => () => abortPendingRequests(), [abortPendingRequests]);

  const beginRequest = useCallback((
    kind: ExclusionAnalysisKind,
    inputFingerprint: string,
  ): ExclusionAnalysisRequest => {
    requestsRef.current[kind]?.controller.abort(supersededRequestReason());
    const request: ExclusionAnalysisRequest = {
      kind,
      requestId: ++nextRequestIdRef.current,
      sessionKey,
      inputFingerprint,
      controller: new AbortController(),
    };
    requestsRef.current[kind] = request;
    return request;
  }, [sessionKey]);

  const isCurrentRequest = useCallback((request: ExclusionAnalysisRequest): boolean => (
    requestsRef.current[request.kind] === request
    && request.sessionKey === sessionKey
    && request.inputFingerprint === currentFingerprintsRef.current[request.kind]
    && !readOnlyRef.current
    && !request.controller.signal.aborted
  ), [sessionKey]);

  const finishRequest = useCallback((request: ExclusionAnalysisRequest) => {
    if (requestsRef.current[request.kind] !== request) return false;
    delete requestsRef.current[request.kind];
    return true;
  }, []);

  const patchExclusions = useCallback((next: GenerationLayoutExclusionSet) => {
    if (readOnlyRef.current) return;
    latestExclusionsRef.current = next;
    onPatch({ exclusions: next });
  }, [onPatch]);

  const fetchFema = useCallback(async () => {
    if (!parcel || readOnly) return;
    const request = beginRequest('fema', femaInputFingerprint);
    setFetching(true);
    try {
      const fetched = await fetchFemaExclusionZones(parcel.polygon, {
        include500yr,
        signal: request.controller.signal,
      });
      if (!isCurrentRequest(request)) return;
      const nonFema = (latestExclusionsRef.current?.zones ?? [])
        .filter(zone => zone.source !== 'fema-nfhl');
      patchExclusions({
        ...fetched,
        zones: [...fetched.zones, ...nonFema],
      });
      if (fetched.femaStatus === 'partial') {
        toast.warning(`FEMA screening incomplete; known exclusions retained: ${fetched.femaUnavailableReason ?? 'coverage unresolved'}`);
      } else if (fetched.femaStatus === 'unavailable') {
        toast.warning(`FEMA flood screening unavailable: ${fetched.femaUnavailableReason ?? 'unknown reason'}`);
      } else if (fetched.zones.length === 0) {
        toast.success('FEMA NFHL reports no flood hazard zones on this parcel.');
      } else {
        toast.success(`Applied ${fetched.zones.length} FEMA flood exclusion zone${fetched.zones.length === 1 ? '' : 's'}.`);
      }
    } catch (error) {
      if (request.controller.signal.aborted || !isCurrentRequest(request)) return;
      toast.error((error as Error).message || 'FEMA flood query failed');
    } finally {
      if (finishRequest(request)) setFetching(false);
    }
  }, [
    beginRequest,
    femaInputFingerprint,
    finishRequest,
    include500yr,
    isCurrentRequest,
    parcel,
    patchExclusions,
    readOnly,
  ]);

  const replaceZonesFromSource = useCallback((
    source: GenerationLayoutExclusionZone['source'],
    zones: GenerationLayoutExclusionZone[],
  ) => {
    const current: GenerationLayoutExclusionSet = latestExclusionsRef.current
      ?? { zones: [], femaStatus: 'not-fetched' };
    patchExclusions({
      ...current,
      zones: [...current.zones.filter(zone => zone.source !== source), ...zones],
    });
  }, [patchExclusions]);

  const fetchWetlands = useCallback(async () => {
    if (!parcel || readOnly) return;
    const request = beginRequest('wetlands', scopeFingerprint);
    setFetchingWetlands(true);
    try {
      const result = await fetchNwiWetlandExclusionZones(parcel.polygon, {
        signal: request.controller.signal,
      });
      if (!isCurrentRequest(request)) return;
      const current = latestExclusionsRef.current ?? { zones: [], femaStatus: 'not-fetched' as const };
      if (result.status === 'unavailable') {
        patchExclusions({ ...current, nwiStatus: 'unavailable', nwiUnavailableReason: result.reason, nwiRetrievedAt: result.retrievedAt });
        toast.warning(`NWI wetlands screening unavailable: ${result.reason}`);
        return;
      }
      patchExclusions({
        ...current, zones: [...current.zones.filter(zone => zone.source !== 'usfws-nwi'), ...result.zones],
        nwiStatus: result.status, nwiUnavailableReason: result.status === 'partial' ? result.reason : undefined,
        nwiRetrievedAt: result.retrievedAt, nwiCoverage: result.coverage,
      });
      if (result.status === 'partial') {
        toast.warning(`Applied ${result.zones.length} mapped NWI zones. Screening remains incomplete: ${result.reason}`);
        return;
      }
      toast.success(result.zones.length === 0
        ? 'No mapped NWI wetlands intersect this parcel within published mapping coverage.'
        : `Applied ${result.zones.length} NWI wetland exclusion zone${result.zones.length === 1 ? '' : 's'}.`);
    } catch (error) {
      if (request.controller.signal.aborted || !isCurrentRequest(request)) return;
      toast.error((error as Error).message || 'NWI wetlands query failed');
    } finally {
      if (finishRequest(request)) setFetchingWetlands(false);
    }
  }, [
    beginRequest,
    finishRequest,
    isCurrentRequest,
    parcel,
    readOnly,
    patchExclusions,
    scopeFingerprint,
  ]);

  const deriveSlope = useCallback(async () => {
    if (!parcel || readOnly) return;
    const request = beginRequest('slope', scopeFingerprint);
    setDerivingSlope(true);
    try {
      const cascade = await sampleElevationCascading(parcel.polygon, {
        signal: request.controller.signal,
      });
      if (!isCurrentRequest(request)) return;
      if (cascade.attemptResult === 'empty' || cascade.samples.length === 0) {
        toast.warning(`Slope screening unavailable: ${cascade.emptyReason ?? 'no elevation samples returned'}`);
        return;
      }
      const derived = deriveSteepSlopeExclusionZones({
        samples: cascade.samples,
        parcelPolygon: parcel.polygon,
        source: cascade.source,
        retrievedAt: new Date().toISOString(),
      });
      if (derived.status === 'insufficient-samples') {
        toast.warning(`Slope screening needs more elevation samples (got ${derived.sampleCount}).`);
        return;
      }
      replaceZonesFromSource('derived-slope', derived.zones);
      toast.success(derived.zones.length === 0
        ? `No ground above ${derived.gradeThresholdPct}% grade detected (max ${derived.maxGradePct ?? 0}%).`
        : `Excluded ground above ${derived.gradeThresholdPct}% grade (${derived.steepSampleCount} steep samples, max ${derived.maxGradePct}%).`);
    } catch (error) {
      if (request.controller.signal.aborted || !isCurrentRequest(request)) return;
      toast.error((error as Error).message || 'Slope derivation failed');
    } finally {
      if (finishRequest(request)) setDerivingSlope(false);
    }
  }, [
    beginRequest,
    finishRequest,
    isCurrentRequest,
    parcel,
    readOnly,
    replaceZonesFromSource,
    scopeFingerprint,
  ]);

  const toggleSiteLayer = useCallback((layer: SiteLayer, enabled: boolean) => {
    const zoneId = `site-layer-${layer.id}`;
    const current: GenerationLayoutExclusionSet = exclusions
      ?? { zones: [], femaStatus: 'not-fetched' };
    patchExclusions({
      ...current,
      zones: enabled
        ? [...current.zones.filter(zone => zone.id !== zoneId), siteLayerZone(layer)]
        : current.zones.filter(zone => zone.id !== zoneId),
    });
  }, [exclusions, patchExclusions]);

  const femaStatusLine = !exclusions || exclusions.femaStatus === 'not-fetched'
    ? 'Not fetched'
    : exclusions.femaStatus === 'partial'
      ? `Incomplete · ${femaZones.length} known zone${femaZones.length === 1 ? '' : 's'} — ${exclusions.femaUnavailableReason ?? 'coverage unresolved'}`
    : exclusions.femaStatus === 'unavailable'
      ? `Unavailable — ${exclusions.femaUnavailableReason ?? 'unknown reason'}`
      : exclusions.femaStatus === 'disabled'
        ? 'Disabled'
        : `Applied · ${femaZones.length} zone${femaZones.length === 1 ? '' : 's'}${exclusions.retrievedAt ? ` · ${exclusions.retrievedAt.slice(0, 10)}` : ''}`;

  return (
    <div className="mt-3 border border-border p-3 rounded-lg" data-testid="generation-layout-exclusions">
      <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-text-secondary">
        <ShieldX className="h-3.5 w-3.5 text-primary" aria-hidden /> No-build exclusions
      </p>
      <p className="mt-1 text-[10px] text-text-muted">
        Recorded zones are subtracted from the buildable area before placement; the packer relocates
        the layout off excluded ground where room allows.
      </p>

      <button
        type="button"
        disabled={readOnly || fetching || !parcel}
        onClick={() => void fetchFema()}
        className="mt-2 flex w-full items-center justify-center gap-1.5 border border-border px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary disabled:opacity-40 rounded-md"
        data-testid="generation-fetch-fema-exclusions"
      >
        <CloudRain className="h-3 w-3" aria-hidden />
        {fetching ? 'Querying FEMA NFHL…' : 'Fetch FEMA flood exclusions'}
      </button>
      <label className="mt-2 flex items-center gap-2 text-[10px] normal-case text-text-secondary">
        <input
          type="checkbox"
          checked={include500yr}
          disabled={readOnly || fetching}
          onChange={(event) => setInclude500yr(event.target.checked)}
        />
        Also exclude 0.2% annual chance (500-yr) zones
      </label>
      <p className="mt-2 text-[10px] text-text-muted">
        FEMA status: <span className={(exclusions?.femaStatus === 'unavailable' || exclusions?.femaStatus === 'partial') ? 'font-bold text-warning' : 'font-bold'}>{femaStatusLine}</span>
      </p>

      <button
        type="button"
        disabled={readOnly || fetchingWetlands || !parcel}
        onClick={() => void fetchWetlands()}
        className="mt-2 flex w-full items-center justify-center gap-1.5 border border-border px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary disabled:opacity-40 rounded-md"
        data-testid="generation-fetch-nwi-exclusions"
      >
        <Sprout className="h-3 w-3" aria-hidden />
        {fetchingWetlands ? 'Querying NWI wetlands…' : 'Fetch NWI wetland exclusions'}
      </button>
      <button
        type="button"
        disabled={readOnly || derivingSlope || !parcel}
        onClick={() => void deriveSlope()}
        className="mt-2 flex w-full items-center justify-center gap-1.5 border border-border px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary disabled:opacity-40 rounded-md"
        data-testid="generation-derive-slope-exclusions"
      >
        <Mountain className="h-3 w-3" aria-hidden />
        {derivingSlope ? 'Sampling elevations…' : 'Derive steep-slope exclusions'}
      </button>

      {nonFemaZones.length > 0 && (
        <ul className="mt-2 space-y-0.5 border-t border-border pt-2 text-[10px] text-text-secondary">
          {nonFemaZones.map(zone => (
            <li key={zone.id} className="truncate" title={zone.label}>• {zone.label}</li>
          ))}
        </ul>
      )}

      {constraintLayers.length > 0 && (
        <div className="mt-2 border-t border-border pt-2">
          <p className="text-[10px] uppercase text-text-muted">Site constraint layers</p>
          {constraintLayers.map(layer => {
            const active = (exclusions?.zones ?? []).some(zone => zone.id === `site-layer-${layer.id}`);
            return (
              <label key={layer.id} className="mt-1 flex items-center gap-2 text-[10px] normal-case text-text-secondary">
                <input
                  type="checkbox"
                  checked={active}
                  disabled={readOnly}
                  onChange={(event) => toggleSiteLayer(layer, event.target.checked)}
                />
                {layer.name}
              </label>
            );
          })}
        </div>
      )}

      {(exclusions?.zones.length ?? 0) > 0 && (
        <button
          type="button"
          disabled={readOnly}
          onClick={() => onPatch({ exclusions: undefined })}
          className="mt-2 flex w-full items-center justify-center gap-1.5 border border-border px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary disabled:opacity-40 rounded-md"
          data-testid="generation-clear-exclusions"
        >
          <Trash2 className="h-3 w-3" aria-hidden /> Clear exclusions
        </button>
      )}
    </div>
  );
};
